"use client"
import { useEffect, useRef, useState, useId } from "react"
import Script from "next/script"
// import { ChartData } from "@/lib/generated/graphql" // No longer needed directly
import type { Chart } from "@/lib/generated/graphql"

// Add type declaration for window.google
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    google: any
  }
}

export default function LineChart({ data }: { data: Chart["dataSources"] }) {
  const chartRef = useRef<HTMLDivElement>(null)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)
  const [selectedChartIndex, setSelectedChartIndex] = useState(0)

  // Generate unique IDs for dashboard elements
  const dashboardId = useId()
  const chartDivId = useId()
  const filterDivId = useId()

  const handleScriptLoad = () => {
    setIsScriptLoaded(true)
    // Immediately load Google Charts after script loads
    if (window.google && window.google.charts && data && data.length > 0) {
      window.google.charts.load("current", { packages: ["corechart", "controls"] })
    }
  }

  // Check if Google Charts is already available when component mounts
  useEffect(() => {
    if (window.google && window.google.charts && window.google.visualization) {
      setIsScriptLoaded(true)
    }
  }, []) // Empty dependency array means this runs once on mount

  // Auto-select the first chart when data becomes available
  useEffect(() => {
    if (data && data.length > 0) {
      setSelectedChartIndex(0) // Explicitly set to first chart
    }
  }, [data])

  useEffect(() => {
    // Ensure script is loaded, refs are available, and data exists for the selected index
    if (!isScriptLoaded || !chartRef.current || !data || !data[selectedChartIndex]) return

    // Get dashboard, chart, and filter div elements using generated IDs
    const dashboardDiv = document.getElementById(dashboardId)
    const chartDiv = document.getElementById(chartDivId)
    const filterDiv = document.getElementById(filterDivId)

    if (!dashboardDiv || !chartDiv || !filterDiv) return

    // Clear previous chart/filter content before drawing
    chartDiv.innerHTML = ""
    filterDiv.innerHTML = ""

    const google = window.google
    google.charts.load("current", { packages: ["corechart", "controls"] })
    google.charts.setOnLoadCallback(drawChart)

    function drawChart() {
      // Check if we're on mobile
      const isMobile = window.innerWidth < 768 // Standard breakpoint for mobile

      // Adjust chart container height based on device
      const chartContainer = document.getElementById(chartDivId)
      if (chartContainer) {
        chartContainer.style.height = isMobile ? "300px" : "400px" // 300px for mobile, 400px for desktop
      }

      // Calculate chart area height percentage based on device
      const chartAreaHeight = isMobile ? "75%" : "80%" // 75% of 300px = 225px on mobile, 80% of 400px = 320px on desktop

      // Ensure data for the selected index exists
      const selectedChartData = data[selectedChartIndex]
      // Add check for empty points array
      if (
        !selectedChartData ||
        !selectedChartData.points ||
        selectedChartData.points.length === 0 ||
        !chartDiv ||
        !filterDiv
      )
        return

      // Prepare the data for Google Charts for the selected chart
      const dataArray: (string | number | Date)[][] = [
        // First row is column headers
        ["Date", selectedChartData.title],
      ]

      // Process points for the selected dataset
      selectedChartData.points.forEach(([epoch, value]) => {
        dataArray.push([new Date(epoch), value])
      })

      // Check if there's actual data beyond the header row
      if (dataArray.length <= 1) {
        console.warn("No data points available for the selected chart:", selectedChartData.title)
        // Optionally clear the chart divs or display a message
        chartDiv.innerHTML = "<p>No data available for this chart.</p>"
        filterDiv.innerHTML = "" // Clear filter div as well
        return // Stop execution if no data
      }

      try {
        const chartData = google.visualization.arrayToDataTable(dataArray)

        // Create a dashboard.
        const dashboard = new google.visualization.Dashboard(dashboardDiv)

        // Create the line chart.
        const lineChart = new google.visualization.ChartWrapper({
          chartType: "LineChart",
          containerId: chartDivId, // Use the specific chart div ID
          options: {
            title: selectedChartData.title,
            titleTextStyle: {
              color: "black",
              fontSize: 18,
            },
            curveType: "function",
            legend: { position: "none" }, // Legend handled by dropdown
            colors: ["#18468B"], // Use a single color or cycle if needed
            chartArea: {
              width: "100%",
              height: chartAreaHeight, // Use the dynamically calculated height
            },
            hAxis: {
              gridlines: { color: "transparent", count: 5 }, // Limit gridlines to approx 5
              format: "MMM yyyy", // Example date format
            },
            // Define two vertical axes: 0 (left, unused) and 1 (right, used)
            vAxes: {
              0: { textPosition: "none" }, // Hide left axis labels/ticks
              1: {
                title: "", // No title for the right axis
                gridlines: { color: "#c8cacf" }, // use our border grey produces odd effects
                textPosition: "in", // Show labels outside the chart area on the right
                format: "#.0", // Format numbers to one decimal place
                viewWindowMode: "explicit",
                viewWindow: {
                  min: null, // Auto-calculate minimum
                  max: null, // Auto-calculate maximum
                },
              },
            },
            lineWidth: 2,
            // Map the first data series (index 0) to the second vAxis (index 1)
            series: { 0: { targetAxisIndex: 1 } },
            tooltip: { isHtml: true },
            pointSize: 5,
            pointsVisible: false,
          },
        })

        // Determine the full range
        const firstDataPointDate = new Date(selectedChartData.points[0][0])
        const rangeFilterEnd = new Date(selectedChartData.points[selectedChartData.points.length - 1][0])

        // Calculate the date one year before the end date
        const oneYearBeforeEnd = new Date(rangeFilterEnd)
        oneYearBeforeEnd.setFullYear(oneYearBeforeEnd.getFullYear() - 1)

        // Set the initial start date: one year ago, but not before the first data point
        const rangeFilterStart = oneYearBeforeEnd > firstDataPointDate ? oneYearBeforeEnd : firstDataPointDate

        // Generate ticks for every year in the filter range
        const yearTicks = []
        const startYear = firstDataPointDate.getFullYear()
        const endYear = rangeFilterEnd.getFullYear()
        for (let year = startYear; year <= endYear; year++) {
          // Add a tick for Jan 1st of each year within the range
          yearTicks.push(new Date(year, 0, 1))
        }

        // Create a range slider, using the first column as the filter control.
        const control = new google.visualization.ControlWrapper({
          controlType: "ChartRangeFilter",
          containerId: filterDivId, // Use the specific filter div ID
          // Set the initial state to the calculated range (last year or full range if less than a year)
          state: {
            range: {
              start: rangeFilterStart,
              end: rangeFilterEnd,
            },
          },
          options: {
            filterColumnIndex: 0,
            ui: {
              chartType: "LineChart",
              chartOptions: {
                chartArea: { width: "100%", height: "100%" }, // Use full width for filter chart area
                hAxis: {
                  baselineColor: "none",
                  format: "yyyy", // Show only year in filter axis
                  ticks: yearTicks, // Explicitly set ticks for each year
                },
                colors: ["#18468B"],
                lineWidth: 1,
              },
              // Display half the data range by default. Adjust as needed.
              snapToData: true, // Snap handles to the nearest data point
            },
          },
        })

        // Establish dependencies, declaring that 'control' drives 'lineChart',
        // so that the line chart redraws whenever the control changes.
        dashboard.bind(control, lineChart)

        // Draw the dashboard.
        dashboard.draw(chartData)
      } catch (error) {
        console.error("Error drawing Google Chart:", error)
        // Display error in the chart div
        // Ensure error has a message property before accessing it
        const errorMessage = error instanceof Error ? error.message : String(error)
        chartDiv.innerHTML = `<p class="text-red-600">Error rendering chart: ${errorMessage}</p>`
        filterDiv.innerHTML = "" // Clear filter div on error
      }
    }

    // Handle window resize for responsiveness
    const handleResize = () => {
      drawChart()
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [isScriptLoaded, data, selectedChartIndex, dashboardId, chartDivId, filterDivId]) // Add dependencies

  return (
    <div className="chart-container w-full">
      <Script src="https://www.gstatic.com/charts/loader.js" onLoad={handleScriptLoad} strategy="afterInteractive" />

      {/* Dropdown for chart selection */}
      {data && data.length > 1 && (
        <div className="mb-4">
          <label htmlFor="chart-selector" className="mr-2 font-semibold">
            Select chart:
          </label>
          <select
            id="chart-selector"
            value={selectedChartIndex}
            onChange={(e) => setSelectedChartIndex(parseInt(e.target.value, 10))}
            className="border-interest-light-blue border p-2"
          >
            {data.map((chartItem, index) => (
              <option key={index} value={index}>
                {chartItem.tabTitle || chartItem.title} {/* Use tabTitle or fallback to title */}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Dashboard container */}
      <div ref={chartRef} id={dashboardId} className="w-full">
        {/* Div for the main chart with relative positioning for overlay */}
        <div className="relative w-full">
          {/* The main chart div */}
          <div id={chartDivId} className="w-full" aria-label="Line chart" />

          {/* Source attribution overlay */}
          {data && data[selectedChartIndex] && data[selectedChartIndex].source && (
            <div className="absolute bottom-11 left-5 bg-white bg-opacity-70 px-1 text-xs text-gray-600">
              Source:{" "}
              {data[selectedChartIndex].sourceUrl ? (
                <a
                  href={data[selectedChartIndex].sourceUrl}
                  target="_blank"
                  rel="noopener"
                  className="text-[rgb(24,70,139)] hover:underline"
                >
                  {data[selectedChartIndex].source}
                </a>
              ) : (
                <span>{data[selectedChartIndex].source}</span>
              )}
            </div>
          )}
        </div>

        {/* Div for the range filter chart */}
        <div id={filterDivId} className="mt-2 h-[100px] w-full" aria-label="Chart range filter" />
      </div>
    </div>
  )
}
